// Which riding atlases a scene needs (bike plan 3, task 4), as plain data: bikeArt.js loads and releases them. Only
// what bike_rig.json lists has an atlas (scripts/anim/bike/export.py); anything else is left out, so a missing
// layer set never turns into a 404.
import { baseBikeAtlas } from './bikeRiderLayout.mjs';
import { ownedBikes } from './ownedBikes.mjs';
import { hairKeys } from '../look/hairArt.mjs';

// the bike's atlases: the default design's (base parts, discs, kickstand), then the chosen design's when it differs
export function parkedArtKeys({ rig, type, design }) {
  const designs = rig.designs?.[type];
  if (!designs?.default) return [];
  const keys = [baseBikeAtlas(rig, type)];
  if (design && design !== designs.default && designs.ids?.includes(design)) keys.push(`bike_${type}_${design}`);
  return keys;
}

// The clothes a character has on (gameState's inventory.equipped, {category: clothing id}) that have ride layers,
// as riderLayers' {id, slot}: the equipped ids are the rig's item ids.
export function wornBikeItems(rig, char, equipped) {
  const owned = rig.items?.[char] ?? {};
  return Object.values(equipped ?? {}).filter((id) => id && owned[id]).map((id) => ({ id, slot: owned[id].slot }));
}

// The rider's hair atlas ({front, back}: one atlas, two frames per view): their style's, under a hat its hat variant.
export function riderHair(char, look, items = []) {
  return hairKeys('bike', char, look, { akcesorium: items.find((it) => it.slot === 'accessory')?.id ?? null });
}

// The default style's hair atlas: what a rider whose own style's atlas does not load wears instead (never bald).
export const riderHairFallback = (char) => hairKeys('bike', char, null, null);

// a rider on the bike: the body and its hair, the bike, the clothes worn that have ride layers
export function bikeArtKeys({ rig, char, type, design, items = [], look = null }) {
  const owned = rig.items?.[char] ?? {};
  return [
    ...(rig.rows?.[char] ? [`bike_rider_${char}`, riderHair(char, look, items).front] : []),
    ...parkedArtKeys({ rig, type, design }),
    ...items.filter((it) => owned[it.id]).map((it) => `bike_cloth_${char}_${it.id}`),
  ];
}

// What one BikeSprite needs now: nothing while hidden (a scene builds its rider and both parked bikes up front, and
// a peer's bike waits for them to ride), else the parked bike's or the rider's atlases.
export function spriteArtKeys({ rig, shown, parked, char, type, design, items = [], look = null }) {
  if (!shown) return [];
  return parked ? parkedArtKeys({ rig, type, design }) : bikeArtKeys({ rig, char, type, design, items, look });
}

// What a scene change keeps (bikeArt.js releaseBikeArt): the player's own bikes in their designs, and their rider
// while they ride (the next scene resumes the ride at once). bikes: gameState's bikes, keyed by design id
// ({id: {owned, type, design}}, bike plan 6); ridingId: the bike being ridden; equipped: inventory.equipped.
// A visited host's and the peers' art goes; they load theirs again on demand.
export function ownBikeArtKeep({ rig, bikes, char, equipped, riding, ridingId, look = null }) {
  const mine = ownedBikes(bikes).filter((b) => rig.designs?.[b.type]);
  const keep = new Set(mine.flatMap((b) => parkedArtKeys({ rig, type: b.type, design: b.design })));
  const ridden = riding ? mine.find((b) => b.id === ridingId) : null;
  if (ridden) {
    const items = wornBikeItems(rig, char, equipped);
    for (const k of bikeArtKeys({ rig, char, type: ridden.type, design: ridden.design, items, look })) keep.add(k);
  }
  return keep;
}

// The loaded riding atlases to drop when the scene changes: riders, clothes and other designs the next scene does
// not keep. The default designs' atlases stay (every design draws its discs and kickstand from them), and keys that
// are not riding atlases (the shop's bike_<type> pictures, bike_grease) are not ours to drop.
export function bikeArtToRelease(loaded, keep, rig) {
  const designKeys = new Set(Object.entries(rig.designs ?? {}).flatMap(([type, d]) =>
    (d.ids ?? []).filter((id) => id !== d.default).map((id) => `bike_${type}_${id}`)));
  return loaded.filter((k) => !keep.has(k)
    && (k.startsWith('bike_rider_') || k.startsWith('bike_hair_') || k.startsWith('bike_cloth_') || designKeys.has(k)));
}
